/**
 * The plant's planes as the estimate measured them: one row per orientation
 * with what stands on it and what it collected, and the plant line under it.
 *
 * The same columns as the per-orientation table in the feasibility report's
 * methodology appendix (pv_core.orientation_report), so the page and the
 * report agree. Drawn only when the plant has more than one plane: a
 * single-plane estimate shows nothing new.
 */
import { Box, Typography } from "@mui/material";
import { autoName, bearing } from "../config/orientations";

const formatValue = (value, digits = 0, locale = "en-GB") =>
  value == null || Number.isNaN(Number(value))
    ? "—"
    : Number(value).toLocaleString("en-GB", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });

/** The plant line: sums where a sum means something, the kWp-weighted
 * incident irradiation where it does not (pv_core.orientation_report.totals). */
export function orientationTotals(rows) {
  const list = (rows || []).filter((r) => r && typeof r === "object");
  if (!list.length) return null;
  const sum = (key) => {
    const values = list.map((r) => r[key]).filter((v) => v != null && !Number.isNaN(Number(v)));
    return values.length ? values.reduce((a, b) => a + Number(b), 0) : null;
  };
  const inverters = new Set();
  list.forEach((r) => (r.inverter_keys || []).forEach((k) => inverters.add(k)));
  const weighted = list.filter((r) => r.poa_kwh_m2 != null && Number(r.kwp) > 0);
  const kwp = sum("kwp");
  const dc = sum("dc_mwh");
  return {
    inverters: inverters.size,
    mppts: list.reduce((a, r) => a + (Number(r.mppts) || 0), 0),
    modules: sum("modules"),
    kwp,
    poa_kwh_m2: weighted.length
      ? weighted.reduce((a, r) => a + Number(r.poa_kwh_m2) * Number(r.kwp), 0)
        / weighted.reduce((a, r) => a + Number(r.kwp), 0)
      : null,
    dc_mwh: dc,
    specific_yield_kwh_kwp: dc != null && kwp ? (1000 * dc) / kwp : null,
  };
}

export function orientationRowName(row, isGreek = false) {
  if (!row) return "";
  if (row.auto_name || !row.name) return autoName(row.tilt_deg, row.azimuth_deg, isGreek);
  return row.name;
}

export default function OrientationYieldTable({ rows, isGreek = false }) {
  const nf = (value, digits) => formatValue(value, digits, isGreek ? "el-GR" : "en-GB");
  const withShare = (value, digits, share) =>
    nf(value, digits) + (share != null && value != null ? ` (${nf(share, 1)}%)` : "");
  const list = (Array.isArray(rows) ? rows : []).filter((r) => r && typeof r === "object");
  if (list.length < 2) return null;
  const tr = (el, en) => (isGreek ? el : en);
  const total = orientationTotals(list);
  const plane = (r) => {
    let text = `${nf(r.tilt_deg, 1)}° / ${nf(r.azimuth_deg, 1)}°`;
    const point = bearing(r.azimuth_deg, isGreek);
    if (point) text += ` (${point})`;
    if (r.design_tilt_deg != null) {
      text += ` — ${tr("βάση στήριξης", "racking")} ${nf(r.design_tilt_deg, 1)}° / ${nf(r.design_azimuth_deg, 1)}°`;
    }
    return text;
  };
  const spacing = (r) => (r.pitch_m == null ? "—"
    : `${nf(r.pitch_m, 2)} m${r.gcr != null ? ` · GCR ${nf(r.gcr, 3)}` : ""}`);
  const attached = (r) => `${nf(r.inverters)} · ${nf(r.mppts)} · ${nf(r.modules)}`;

  return (
    <Box data-testid="orientation-yield-table">
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {tr("Ανά προσανατολισμό", "By orientation")}
      </Typography>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {tr(
          "Κάθε επίπεδο του σταθμού, με ό,τι είναι συνδεδεμένο σε αυτό και ό,τι συνέλεξε σε αυτή την εκτίμηση. Η ενέργεια DC μετριέται στην είσοδο του αντιστροφέα.",
          "Every plane of the plant, with what is attached to it and what it collected in this estimate. DC energy is measured at the inverter input.",
        )}
      </Typography>
      <Box sx={{ overflowX: "auto", mt: 1 }}>
        <Box component="table" sx={{
          width: "100%", borderCollapse: "collapse", fontSize: "0.82rem",
          "& td, & th": {
            textAlign: "left", padding: "5px 10px 5px 0",
            borderBottom: "1px solid", borderColor: "divider",
            verticalAlign: "top", whiteSpace: "nowrap",
          },
          "& td.n, & th.n": { textAlign: "right", fontVariantNumeric: "tabular-nums" },
          "& tfoot td": { fontWeight: 700, borderBottom: 0 },
        }}>
          <Box component="thead">
            <Box component="tr">
              <Box component="th">{tr("Προσανατολισμός", "Orientation")}</Box>
              <Box component="th">{tr("Κλίση / αζιμούθιο", "Tilt / azimuth")}</Box>
              <Box component="th">{tr("Βήμα σειρών · GCR", "Row pitch · GCR")}</Box>
              <Box component="th" className="n">{tr("Αντιστροφείς · MPPT · πλαίσια", "Inverters · MPPTs · modules")}</Box>
              <Box component="th" className="n">{tr("kWp (μερίδιο)", "kWp (share)")}</Box>
              <Box component="th" className="n">{tr("Προσπίπτουσα kWh/m² (× οριζόντια)", "Incident kWh/m² (× horizontal)")}</Box>
              <Box component="th" className="n">{tr("DC MWh (μερίδιο)", "DC MWh (share)")}</Box>
              <Box component="th" className="n">DC kWh/kWp</Box>
            </Box>
          </Box>
          <Box component="tbody">
            {list.map((r) => (
              <Box component="tr" key={r.id || orientationRowName(r)}>
                <Box component="td" sx={{ whiteSpace: "normal !important" }}>{orientationRowName(r, isGreek)}</Box>
                <Box component="td">{plane(r)}</Box>
                <Box component="td">{spacing(r)}</Box>
                <Box component="td" className="n">{attached(r)}</Box>
                <Box component="td" className="n">{withShare(r.kwp, 1, r.kwp_share_pct)}</Box>
                <Box component="td" className="n">
                  {nf(r.poa_kwh_m2)}{r.transposition_factor != null ? ` (×${nf(r.transposition_factor, 2)})` : ""}
                </Box>
                <Box component="td" className="n">{withShare(r.dc_mwh, 1, r.dc_share_pct)}</Box>
                <Box component="td" className="n">{nf(r.specific_yield_kwh_kwp)}</Box>
              </Box>
            ))}
          </Box>
          {total ? (
            <Box component="tfoot">
              <Box component="tr">
                <Box component="td">{tr("Σταθμός", "Plant")}</Box>
                <Box component="td" />
                <Box component="td" />
                <Box component="td" className="n">{attached(total)}</Box>
                <Box component="td" className="n">{nf(total.kwp, 1)}</Box>
                <Box component="td" className="n">{nf(total.poa_kwh_m2)}</Box>
                <Box component="td" className="n">{nf(total.dc_mwh, 1)}</Box>
                <Box component="td" className="n">{nf(total.specific_yield_kwh_kwp)}</Box>
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
